export type WaveSim = {
  width: number;
  height: number;
  visWidth: number;
  visHeight: number;
  pad: number;
  u: Float32Array;
  prev: Float32Array;
  next: Float32Array;
  edge: Float32Array;
};

export type WaveStepParams = {
  c2: number;
  damp: number;
};

export type WaveRenderParams = {
  base: number;
  heightGain: number;
  lightGain: number;
  clampMin: number;
  clampMax: number;
};

export function createWaveSim(
  visWidth: number,
  visHeight: number,
  edgeWidth: number,
  pad: number,
): WaveSim {
  const width = visWidth + pad * 2;
  const height = visHeight + pad * 2;
  const n = width * height;
  const edge = new Float32Array(n);
  const lastX = width - 1;
  const lastY = height - 1;
  const band = Math.max(1, edgeWidth);
  for (let y = 0; y < height; y += 1) {
    const edgeY = Math.min(y, lastY - y);
    const row = y * width;
    for (let x = 0; x < width; x += 1) {
      const edgeX = Math.min(x, lastX - x);
      const dist = Math.min(edgeX, edgeY);
      edge[row + x] = dist < band ? 0.78 + 0.22 * (dist / band) : 1;
    }
  }
  return {
    width,
    height,
    visWidth,
    visHeight,
    pad,
    u: new Float32Array(n),
    prev: new Float32Array(n),
    next: new Float32Array(n),
    edge,
  };
}

export function addOutgoingRing(
  sim: WaveSim,
  nx: number,
  ny: number,
  radius: number,
  sigma: number,
  amplitude: number,
  outward: number,
): void {
  const { width, height, visWidth, visHeight, pad, u, prev } = sim;
  const cx = pad + nx * (visWidth - 1);
  const cy = pad + ny * (visHeight - 1);
  const s = Math.max(0.6, sigma);
  const inv = 1 / (2 * s * s);
  const r0 = Math.max(0.8, radius);
  const rPrev = Math.max(0.35, r0 - Math.max(0.35, outward));
  const span = r0 + s * 4;
  const x0 = Math.max(1, Math.floor(cx - span));
  const x1 = Math.min(width - 2, Math.ceil(cx + span));
  const y0 = Math.max(1, Math.floor(cy - span));
  const y1 = Math.min(height - 2, Math.ceil(cy + span));

  for (let y = y0; y <= y1; y += 1) {
    const dy = y - cy;
    const row = y * width;
    for (let x = x0; x <= x1; x += 1) {
      const d = Math.hypot(x - cx, dy);
      const wu = amplitude * Math.exp(-(d - r0) * (d - r0) * inv);
      const wp = amplitude * Math.exp(-(d - rPrev) * (d - rPrev) * inv);
      if (wu < 0.0008 && wp < 0.0008) continue;
      const i = row + x;
      u[i] += wu;
      prev[i] += wp;
    }
  }
}

export function stepWave(sim: WaveSim, params: WaveStepParams): void {
  const { width, height, u, prev, next, edge } = sim;
  const lastX = width - 1;
  const lastY = height - 1;
  const lastRow = lastY * width;
  const c2 = params.c2;
  const damp = params.damp;

  next.fill(0, 0, width);
  next.fill(0, lastRow, lastRow + width);

  for (let y = 1; y < lastY; y += 1) {
    const row = y * width;
    next[row] = 0;
    next[row + lastX] = 0;
    for (let x = 1; x < lastX; x += 1) {
      const i = row + x;
      const lap = u[i - 1] + u[i + 1] + u[i - width] + u[i + width] - 4 * u[i];
      next[i] = (2 * u[i] - prev[i] + c2 * lap) * damp * edge[i];
    }
  }

  sim.prev = u;
  sim.u = next;
  sim.next = prev;
}

export function renderWave(
  sim: WaveSim,
  pixels: Uint8ClampedArray,
  params: WaveRenderParams,
): void {
  const { width, visWidth, visHeight, pad, u } = sim;
  const { base, heightGain, lightGain, clampMin, clampMax } = params;
  let p = 0;

  for (let y = 0; y < visHeight; y += 1) {
    const row = (y + pad) * width + pad;
    const yu = row - width;
    const yd = row + width;
    for (let x = 0; x < visWidth; x += 1) {
      const i = row + x;
      const nx = u[i + 1] - u[i - 1];
      const ny = u[yd + x] - u[yu + x];
      let v = base + u[i] * heightGain + (nx - ny) * lightGain;
      if (v < clampMin) v = clampMin;
      else if (v > clampMax) v = clampMax;
      const g = v | 0;
      pixels[p] = g;
      pixels[p + 1] = g;
      pixels[p + 2] = g;
      pixels[p + 3] = 255;
      p += 4;
    }
  }
}
